import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Check, Star, Loader2, Lock, ArrowRight, Sparkles, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/apiBase';


const CATEGORY_ICONS = {
    'Smartphone': '📱', 'Tv': '📺', 'Laptop': '💻', 'Gamepad2': '🎮',
    'Refrigerator': '🏠', 'Shirt': '👗', 'User': '👔', 'Sparkles': '💄',
    'Home': '🛋️', 'Dumbbell': '⚽', 'Baby': '👶', 'ToyBrick': '🧸',
    'BookOpen': '📚', 'ShoppingCart': '🛒', 'Car': '🚗', 'Wrench': '🔧',
    'PawPrint': '🐾', 'Plane': '✈️', 'Ticket': '🏷️', 'Gift': '🎁',
};

export default function ExclusiveOffersPopup({ onClose, onSaved }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [eligible, setEligible] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCats, setSelectedCats] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef(null);
    const searchTimeout = useRef(null);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const totalSelected = selectedCats.length + selectedProducts.length;
    const maxSelections = 5;
    const canSelect = totalSelected < maxSelections;

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/offer-preferences`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setEligible(data.eligible);
                    setCategories(data.available_categories || []);
                    if (data.categories?.length) setSelectedCats(data.categories);
                    if (data.products?.length) setSelectedProducts(data.products);
                }
            } catch {} finally { setLoading(false); }
        })();
        // Track popup viewed
        fetch(`${API}/offer-preferences/analytics`, {
            method: 'POST', headers,
            body: JSON.stringify({ event_type: 'popup_viewed' }),
        }).catch(() => {});
    }, []);

    const handleSearch = useCallback((q) => {
        setSearchQuery(q);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (q.length < 2) { setSearchResults([]); setShowResults(false); return; }
        setSearching(true);
        setShowResults(true);
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await fetch(`${API}/products/search?q=${encodeURIComponent(q)}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    setSearchResults(data.products || []);
                }
            } catch {} finally { setSearching(false); }
        }, 400);
    }, [token]);

    const toggleCategory = (cat) => {
        const isSelected = selectedCats.some(c => c.slug === cat.slug);
        if (isSelected) {
            setSelectedCats(prev => prev.filter(c => c.slug !== cat.slug));
        } else if (canSelect) {
            setSelectedCats(prev => [...prev, { slug: cat.slug, name: cat.name }]);
        } else {
            toast.error(`Máximo de ${maxSelections} seleções`);
        }
    };

    const addProduct = (product) => {
        if (selectedProducts.some(p => p.name === product.name)) {
            toast.info('Produto já selecionado');
            return;
        }
        if (!canSelect) {
            toast.error(`Máximo de ${maxSelections} seleções`);
            return;
        }
        setSelectedProducts(prev => [...prev, {
            name: product.name,
            category_slug: product.category_slug,
            category_name: product.category_name,
        }]);
        setSearchQuery('');
        setShowResults(false);
        setSearchResults([]);
    };

    const removeProduct = (idx) => {
        setSelectedProducts(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSave = async () => {
        if (totalSelected === 0) { toast.error('Selecione pelo menos 1 categoria ou produto'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/offer-preferences`, {
                method: 'PUT', headers,
                body: JSON.stringify({ categories: selectedCats, products: selectedProducts }),
            });
            if (res.ok) {
                toast.success('Preferências salvas com sucesso!');
                onSaved?.();
                onClose();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Erro ao salvar');
            }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const handleDefer = async () => {
        try {
            await fetch(`${API}/offer-preferences/defer`, { method: 'POST', headers });
        } catch {}
        fetch(`${API}/offer-preferences/analytics`, {
            method: 'POST', headers,
            body: JSON.stringify({ event_type: 'popup_deferred' }),
        }).catch(() => {});
        onClose();
    };

    // Click outside search results
    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    if (loading) return null;

    // Blocked version for non-eligible users
    if (!eligible) {
        return (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }} data-testid="exclusive-offers-popup-blocked">
                <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)' }} onClick={e => e.stopPropagation()}>
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                            <Lock size={28} style={{ color: 'var(--sv-accent)' }} />
                        </div>
                    </div>
                    <h3 className="text-lg font-bold text-center mb-2" style={{ color: '#F5F5F5', fontFamily: "var(--font-display)" }}>Ofertas Exclusivas</h3>
                    <p className="text-sm text-center mb-6" style={{ color: '#888', lineHeight: 1.6 }}>
                        Receba ofertas personalizadas para o seu negócio. Disponível para empresas com plano ativo.
                    </p>
                    <a href="/sistema/empresa/assinatura" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="upgrade-cta-btn">
                        <Crown size={16} /> Fazer Upgrade <ArrowRight size={14} />
                    </a>
                    <button onClick={onClose} className="w-full mt-3 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'transparent', color: '#666', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="close-blocked-btn">Fechar</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }} data-testid="exclusive-offers-popup">
            <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 pb-3" style={{ borderBottom: '1px solid #1e1e1e' }}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', border: '1px solid rgba(var(--sv-accent-rgb), 0.2)' }}>
                                <Sparkles size={18} style={{ color: 'var(--sv-accent)' }} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold" style={{ color: '#F5F5F5', fontFamily: "var(--font-display)" }} data-testid="popup-title">Receba ofertas alinhadas ao seu negócio</h3>
                                <p className="text-xs mt-0.5" style={{ color: '#777' }}>Selecione até {maxSelections} produtos ou categorias para receber ofertas relevantes.</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: '#555' }} data-testid="popup-close-btn"><X size={16} /></button>
                    </div>
                    {/* Selection counter */}
                    <div className="flex items-center gap-2 mt-3">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#1e1e1e' }}>
                            <div className="h-full rounded-full transition-all" style={{ width: `${(totalSelected / maxSelections) * 100}%`, background: totalSelected >= maxSelections ? 'var(--sv-danger)' : 'var(--sv-accent)' }} />
                        </div>
                        <span className="text-xs font-bold shrink-0" style={{ color: totalSelected >= maxSelections ? 'var(--sv-danger)' : 'var(--sv-accent)' }} data-testid="selection-counter">{totalSelected}/{maxSelections}</span>
                    </div>
                </div>

                <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)', padding: '16px 20px' }}>
                    {/* Product Search */}
                    <div className="mb-4" ref={searchRef}>
                        <label className="text-xs font-medium mb-1.5 block" style={{ color: '#999' }}>Buscar produto específico</label>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#555' }} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => handleSearch(e.target.value)}
                                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                                placeholder="Ex: iPhone 15, Air Fryer..."
                                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm"
                                style={{ background: '#071A3A', border: '1px solid rgba(125,165,255,0.16)', color: '#F5F5F5', outline: 'none' }}
                                data-testid="product-search-input"
                            />
                            {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" style={{ color: 'var(--sv-accent)' }} />}
                        </div>
                        {/* Search Results Dropdown */}
                        {showResults && searchResults.length > 0 && (
                            <div className="mt-1 rounded-xl overflow-hidden" style={{ background: '#0D2C63', border: '1px solid rgba(125,165,255,0.16)', maxHeight: 200, overflowY: 'auto' }} data-testid="search-results">
                                {searchResults.map((p, i) => (
                                    <button
                                        key={i}
                                        onClick={() => addProduct(p)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors"
                                        style={{ borderBottom: '1px solid #1e1e1e' }}
                                        onMouseOver={e => e.currentTarget.style.background = '#12397D'}
                                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                        data-testid={`search-result-${i}`}
                                    >
                                        {p.image ? <img src={p.image} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" /> : <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#071A3A' }}><Star size={12} style={{ color: 'var(--sv-accent)' }} /></div>}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm truncate" style={{ color: '#E5E5E5' }}>{p.name}</p>
                                            <p className="text-[10px]" style={{ color: '#666' }}>{p.category_name} {p.store ? `• ${p.store}` : ''}</p>
                                        </div>
                                        {p.price && <span className="text-xs font-bold shrink-0" style={{ color: 'var(--sv-accent)' }}>R$ {p.price}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Selected Products */}
                    {selectedProducts.length > 0 && (
                        <div className="mb-4">
                            <label className="text-xs font-medium mb-1.5 block" style={{ color: '#999' }}>Produtos selecionados</label>
                            <div className="flex flex-wrap gap-2">
                                {selectedProducts.map((p, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)', color: 'var(--sv-accent)' }} data-testid={`selected-product-${i}`}>
                                        <Star size={10} /> {p.name}
                                        <button onClick={() => removeProduct(i)} className="ml-1 hover:opacity-70"><X size={10} /></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Category Grid */}
                    <div>
                        <label className="text-xs font-medium mb-2 block" style={{ color: '#999' }}>Categorias de interesse</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="category-grid">
                            {categories.map(cat => {
                                const isSelected = selectedCats.some(c => c.slug === cat.slug);
                                const icon = CATEGORY_ICONS[cat.icon] || '📦';
                                return (
                                    <button
                                        key={cat.slug}
                                        onClick={() => toggleCategory(cat)}
                                        disabled={!isSelected && !canSelect}
                                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all"
                                        style={{
                                            background: isSelected ? 'rgba(var(--sv-accent-rgb), 0.12)' : '#071A3A',
                                            border: `1px solid ${isSelected ? 'rgba(var(--sv-accent-rgb), 0.35)' : '#1e1e1e'}`,
                                            color: isSelected ? 'var(--sv-accent)' : 'var(--muted-2)',
                                            opacity: !isSelected && !canSelect ? 0.4 : 1,
                                            cursor: !isSelected && !canSelect ? 'not-allowed' : 'pointer',
                                        }}
                                        data-testid={`cat-${cat.slug}`}
                                    >
                                        <span>{icon}</span>
                                        <span className="truncate">{cat.name}</span>
                                        {isSelected && <Check size={12} className="shrink-0 ml-auto" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 flex items-center gap-2" style={{ borderTop: '1px solid #1e1e1e' }}>
                    <button onClick={handleDefer} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'transparent', color: '#666', border: '1px solid rgba(125,165,255,0.16)' }} data-testid="defer-btn">Lembrar depois</button>
                    <button
                        onClick={handleSave}
                        disabled={saving || totalSelected === 0}
                        className="flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                        style={{ background: totalSelected > 0 ? 'var(--sv-action)' : 'var(--surface-3)', color: totalSelected > 0 ? 'var(--sv-on-action)' : 'var(--muted-2)', opacity: saving ? 0.7 : 1 }}
                        data-testid="save-preferences-btn"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar Preferências
                    </button>
                </div>
            </div>
        </div>
    );
}
